import IconVisit from "@common/components/icons/IconVisit";
import { Button, Flex, Loader, Text } from "@mantine/core";
import { getInstanceUrl } from "@stores/instances/utils";
import Link from "next/link";
import { ReactElement } from "react";
import { IGoToInstanceLinkProps } from "./types";
import ErrorMessage from "@common/components/text/ErrorMessage";
import { useRefreshInstanceUntilReady } from "@hooks/useRefreshInstanceUntilReady";

export function GoToInstanceLink({
  purchase,
}: IGoToInstanceLinkProps): ReactElement {
  const { error, instance, isInstanceReady, isSelfHosted } =
    useRefreshInstanceUntilReady(purchase.id);

  // The freshly fetched instance is the authority — the purchase prop is the
  // caller's copy and may predate the plan change — but fall back to it until
  // the first fetch lands.
  const hostedInstance = instance ?? purchase.hosted_instance;

  // self-hosted: no instance to wait for
  if (isSelfHosted || !hostedInstance) {
    return (
      <Button
        data-testid="GoToInstanceLink-healthy"
        component={Link}
        href="/account"
        w="auto"
      >
        Go back to your instances
      </Button>
    );
  }

  // not ready: still restarting, or not yet settled on the new plan
  if (!isInstanceReady) {
    return (
      <>
        <Flex
          data-testid="GoToInstanceLink-changing"
          align="center"
          direction={{ base: "column", sm: "row" }}
          gap="sm"
          justify="center"
          wrap="nowrap"
        >
          <Text c="gray.5" fz="md" ta="center">
            Please wait,
            <Text display={{ base: "none", sm: "inline" }} span>
              &nbsp;
            </Text>
            <Text display={{ base: "inline", sm: "none" }} span>
              <br />
            </Text>
            we are preparing your instance...
          </Text>

          <Loader size="xs" />
        </Flex>

        <ErrorMessage m={0} error={error} />
      </>
    );
  }

  // cloud healthy
  return (
    <Button
      data-testid="GoToInstanceLink-healthy"
      component={Link}
      href={getInstanceUrl(hostedInstance)}
      target="_blank"
      rel="noreferrer"
      w="auto"
    >
      <Text span mr="xs">
        Go back to your Metabase instance
      </Text>
      <IconVisit color="#ffffff" size={10} />
    </Button>
  );
}
